import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, vShow as _vShow, withDirectives as _withDirectives, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, unref as _unref, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-0c31b088"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "resume-builder" }
const _hoisted_2 = { class: "edit-panel" }
const _hoisted_3 = { class: "d-flex gap-2 mb-3" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "bi bi-download me-2" }, null, -1))
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "bi bi-save me-2" }, null, -1))
const _hoisted_6 = { class: "section-content" }
const _hoisted_7 = {
  key: 0,
  class: "save-button-container"
}
const _hoisted_8 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "bi bi-save me-2" }, null, -1))
const _hoisted_9 = {
  key: 6,
  class: "optimize-section"
}
const _hoisted_10 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h3", { class: "section-title" }, "Optimize Your Resume", -1))
const _hoisted_11 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "section-title-underline" }, null, -1))
const _hoisted_12 = { class: "row g-4" }
const _hoisted_13 = { class: "col-12" }

import { ref, watch, onMounted, computed } from "vue";
import { useRouter, useRoute } from "vue-router";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import BreadcrumbNav from "../components/BreadcrumbNav.vue";
import UpgradeResumeModal from "../components/UpgradeResumeModal.vue";

// Import types
import type {
  Resume,
  Project,
  Certification,
  AdditionalSkill,
  WorkExperience,
  Education,
  Activity,
} from "../../../server/src/models/Resume";
import { Tone } from "../../../server/src/models/SystemEnums";
import { ResponseDto, TemplateOptions } from "../../../server/src/models/dtos/ResponseDtos";

// Import components
import ResumeNavigation from "../components/resume-editor/ResumeNavigation.vue";
import ContactSection from "../components/resume-editor/ContactSection.vue";
import SummarySection from "../components/resume-editor/SummarySection.vue";
import WorkExperienceSection from "../components/resume-editor/WorkExperienceSection.vue";
import EducationSection from "../components/resume-editor/EducationSection.vue";
import AdditionalSection from "../components/resume-editor/AdditionalSection.vue";
import PdfPreview from "../components/resume-editor/PdfPreview.vue";
import TemplateSelector from "../components/resume-editor/TemplateSelector.vue";
import DownloadModal from "../components/DownloadModal.vue";
import ApplicationSelectorWithModal from "../components/ApplicationSelectorWithModal.vue"; // Import the new component

// Import services
import { DocumentApiService } from "../services/DocumentApiService";
import { FileApiService } from "../services/FilesApiService";
import { ResponseType } from "../../../server/src/models/Response";
import { ResumeGeneratorService } from "../services/ResumeGeneratorService";
import { ApplicationApiService } from "../services/ApplicationApiService";
import { onClickOutside } from "@vueuse/core";

// Component setup

export default /*#__PURE__*/_defineComponent({
  __name: 'EditResume',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const loading = ref(false);
const pdfUrl = ref<string>("");
const currentSection = ref("contact");
const hasUnsavedChanges = ref(false);
const originalResume = ref<Resume | null>(null);
const selectedWorkIndex = ref(0);
const selectedEducationIndex = ref(0);
const skillInputs = ref<string[]>([]);
const interestInput = ref("");
const techInput = ref("");
const isPreviewPending = ref(false);
const previewDebounceTimeout = ref<number | null>(null);
const downloadModal = ref<InstanceType<typeof DownloadModal> | null>(null);
const upgradeResumeModal = ref<InstanceType<typeof UpgradeResumeModal> | null>(null);
const isLoading = ref(false);
const loadingMessage = ref("Loading...");

// Application data for optimize tab
const loadingApplications = ref(false);

// Computed property for filtered applications
const filteredApplications = computed(() => {
  return [];
});

// Initialize the resume with default values
const resume = ref<Resume>({
  id: "",
  userId: "",
  name: "",
  lastModifiedAt: new Date(),
  previewUrl: null,
  template: TemplateOptions.RESUME_CLASSIC,
  contactInfo: {
    firstName: "",
    surname: "",
    email: "",
    phone: "",
    location: "",
    linkedin: "",
    websiteOrPortfolio: "",
    uuid: crypto.randomUUID(),
  },
  summary: {
    title: "",
    description: "",
    uuid: crypto.randomUUID(),
  },
  workExperience: [] as WorkExperience[],
  education: [] as Education[],
  projects: [] as Project[],
  activities: [] as Activity[],
  certifications: [] as Certification[],
  additionalSkills: [] as AdditionalSkill[],
  languages: [] as string[],
  interests: [] as string[],
});

// Watch for changes in the resume object
watch(
  () => JSON.stringify(resume.value),
  (newValue) => {
    if (originalResume.value) {
      hasUnsavedChanges.value = newValue !== JSON.stringify(originalResume.value);
    }
  },
  { deep: true }
);

// Consolidated preview handling
const updatePreview = async (force = false) => {
  if (!force && currentSection.value !== "preview") return;

  // Clear any pending preview update
  if (previewDebounceTimeout.value) {
    clearTimeout(previewDebounceTimeout.value);
  }

  // Set a debounced preview update
  previewDebounceTimeout.value = window.setTimeout(async () => {
    if (currentSection.value === "preview") {
      isPreviewPending.value = true;
      await previewResume();
      isPreviewPending.value = false;
    }
  }, 300);
};

// Watch for template changes
watch(
  () => resume.value.template,
  () => {
    // Immediately set loading state but keep previous PDF visible
    loading.value = true;
    updatePreview(true);
  }
);

// Watch for section changes
watch(
  () => currentSection.value,
  (newValue) => {
    if (newValue === "preview" && !pdfUrl.value) {
      updatePreview(true);
    }
  }
);

// Watch for resume changes to invalidate the preview
watch(
  () => JSON.stringify(resume.value),
  () => {
    if (pdfUrl.value) {
      URL.revokeObjectURL(pdfUrl.value);
      pdfUrl.value = "";
      updatePreview();
    }
  },
  { deep: true }
);

// Load resume data
onMounted(async () => {
  isLoading.value = true;
  loading.value = true;
  try {
    if (resumeId) {
      // Fetch existing resume
      const response = await DocumentApiService.getUserResume(resumeId);
      resume.value = response.data;
      originalResume.value = JSON.parse(JSON.stringify(response.data));
      
      ensureDefaults(resume.value);

      // Check if resume was created before March 19, 2025
      const resumeReworkDate =  new Date('2025-03-19');
      if (resume.value.id && isObjectIdCreatedBefore(resume.value.id, resumeReworkDate)) {
        // Show upgrade modal
        setTimeout(() => {
          upgradeResumeModal.value?.show();
        }, 500); // Small delay to ensure modal is properly mounted
      }
      else
      {
        ensureUuids(resume.value);
      }
    }
  } catch (error) {
    console.error("Error fetching resume:", error);
    toast.error("Failed to load resume", {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
    });
  } finally {
    isLoading.value = false;
    loading.value = false;
  }
});

function ensureDefaults(resume: Resume) {
  if (!resume.template)
  {
      resume.template = TemplateOptions.RESUME_CLASSIC;
  }
}

// Function to check if MongoDB ObjectId was created before a specific date
function isObjectIdCreatedBefore(objectId: string, targetDate: Date): boolean {
  if (!objectId || objectId.length !== 24) {
    return false;
  }
  
  // Extract timestamp from MongoDB ObjectId (first 4 bytes)
  const timestamp = parseInt(objectId.substring(0, 8), 16);
  
  // Convert to milliseconds (MongoDB timestamp is in seconds)
  const creationDate = new Date(timestamp * 1000);
  
  return creationDate < targetDate;
}

// Handle resume upgrade completion
function handleUpgradeComplete(updatedResume: Resume) {
  resume.value = updatedResume;
  toast.success("Resume upgraded successfully", {
    position: "top-right",
    autoClose: 3000,
  });

  originalResume.value = JSON.parse(JSON.stringify(updatedResume));
}

// Handle viewing the new resume
function viewNewResume(newResumeId: string) {
  router.push({
    name: "EditResume",
    params: { id: newResumeId }
  });
}

// Save resume changes
async function saveResume() {
  try {
    isLoading.value = true;
    loadingMessage.value = "Saving your resume...";

    const resumeData = { ...resume.value };
    await DocumentApiService.updateUserResume(resumeData);

    // Update last modified time
    resume.value.lastModifiedAt = new Date();

    // Reset unsaved changes flag
    hasUnsavedChanges.value = false;
    originalResume.value = JSON.parse(JSON.stringify(resume.value));
    // Update preview after saving
    updatePreview(true);
  } catch (error) {
    console.error("Error saving resume:", error);
    toast.error("Failed to save resume");
  } finally {
    isLoading.value = false;
  }
}

// Generate PDF preview
async function previewResume() {
  try {
    // Don't set loading.value here since we already set it in the template watcher
    // This prevents the PDF from disappearing during template changes
    if (!loading.value) {
      loading.value = true;
    }
    isLoading.value = true;
    loadingMessage.value = "Generating preview...";

    const resumeData = { ...resume.value };
    const response = await FileApiService.getPDFPreview(
      new ResponseDto(
        null,
        "",
        "",
        resume.value.userId,
        false,
        ResponseType.CreateResume,
        null,
        0.7,
        Tone.FORMAL,
        resume.value.template,
        "",
        resume.value,
        null,
        null
      )
    );
    const blob = new Blob([response.data], { type: "application/pdf" });
    if (pdfUrl.value) {
      URL.revokeObjectURL(pdfUrl.value);
    }
    pdfUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error generating PDF preview:", error);
    toast.error("Failed to generate PDF preview");
  } finally {
    loading.value = false;
    isLoading.value = false;
  }
}

const openAccordionSections = ref<string[]>([]);

function toggleAccordion(sectionId: string) {
  const index = openAccordionSections.value.indexOf(sectionId);
  if (index === -1) {
    openAccordionSections.value.push(sectionId);
  } else {
    openAccordionSections.value.splice(index, 1);
  }
}

function isAccordionOpen(sectionId: string) {
  return openAccordionSections.value.includes(sectionId);
}

// Watch for preview mode and open first section
watch(
  () => currentSection.value,
  (newValue) => {
    if (newValue === "preview" && openAccordionSections.value.length === 0) {
      // Open the first non-preview section by default
      openAccordionSections.value.push("contact");
    }
  }
);

// Update the template selection function
function selectTemplate(template: TemplateOptions) {
  if (resume.value) {
    resume.value.template = template;
    // Show save button since template is part of resume data
    if (originalResume.value) {
      hasUnsavedChanges.value =
        JSON.stringify(resume.value) !== JSON.stringify(originalResume.value);
    }
  }
}

const selectedWork = computed(() => {
  const workExperience = resume.value.workExperience || [];
  if (!workExperience.length) return null;
  const work = workExperience[selectedWorkIndex.value];
  if (!work) return null;
  return {
    ...work,
    achievements: work.achievements || [],
  };
});

// Watch for changes in workExperience length
watch(
  () => resume.value?.workExperience?.length,
  (newLength) => {
    if (!newLength) {
      selectedWorkIndex.value = -1;
    } else if (selectedWorkIndex.value >= newLength) {
      selectedWorkIndex.value = newLength - 1;
    }
  }
);

const selectedEducation = computed(() => {
  if (!resume.value?.education?.length) return null;
  return resume.value.education[selectedEducationIndex.value] || null;
});

// Watch for changes in education length
watch(
  () => resume.value?.education?.length,
  (newLength) => {
    if (!newLength) {
      selectedEducationIndex.value = -1;
    } else if (selectedEducationIndex.value >= newLength) {
      selectedEducationIndex.value = newLength - 1;
    }
  }
);

// Computed property for filtered languages
const filteredLanguages = computed(() => {
  return resume.value.languages?.filter((lang) => lang?.trim() !== "") || [];
});

// Add this near the other computed properties
const filteredInterests = computed(() => {
  return resume.value.interests?.filter((interest) => interest?.trim() !== "") || [];
});

// Add function to generate UUIDs for new items
function generateUuid(): string {
  return crypto.randomUUID();
}

// Update addSkillItem to include UUID
function addSkillItem(index: number) {
  if (!skillInputs.value[index] || !skillInputs.value[index].trim()) return;

  const skills = resume.value.additionalSkills || [];
  if (!skills[index]) {
    skills[index] = {
      skillGroup: "",
      skillDetails: [],
      uuid: generateUuid(),
    };
  }

  if (!skills[index].skillDetails) {
    skills[index].skillDetails = [];
  }
  skills[index].skillDetails.push(skillInputs.value[index].trim());

  skillInputs.value[index] = "";
}

function removeSkillItem(groupIndex: number, skillIndex: number) {
  const skills = resume.value.additionalSkills || [];
  if (!skills[groupIndex] || !skills[groupIndex].skillDetails) return;

  // Filter out the skill at the specified index
  skills[groupIndex].skillDetails = skills[groupIndex].skillDetails.filter(
    (_, i: number) => i !== skillIndex
  );
}

function handleSkillBackspace(index: number) {
  if (!skillInputs.value[index]) {
    const skills = resume.value.additionalSkills || [];
    if (!skills[index] || !skills[index].skillDetails) return;

    // Remove the last skill in the array
    skills[index].skillDetails.pop();
  }
}

function handleInterestBackspace(event: KeyboardEvent) {
  if (!resume.value.interests) return;
  if (event.key === "Backspace" && !interestInput.value && resume.value.interests.length > 0) {
    event.preventDefault();
    resume.value.interests = resume.value.interests.slice(0, -1);
  }
}

// Computed property for each skill group's skills
function getFilteredSkills(skillDetails: string[] | undefined) {
  return skillDetails || [];
}

const resumeId = route.params.id as string;

function addTechnology(project: Project) {
  if (!techInput.value || !techInput.value.trim()) return;

  if (!project.achievements) {
    project.achievements = [];
  }

  project.achievements.push({
    achievement: `Technology: ${techInput.value.trim()}`,
    uuid: generateUuid(),
  });

  techInput.value = "";
}

function downloadDocument() {
  downloadModal.value?.show();
}

// Add these computed properties before the template
const workExperience = computed({
  get: () => resume.value.workExperience || [],
  set: (value) => {
    resume.value.workExperience = value;
  },
});

const education = computed({
  get: () => resume.value.education || [],
  set: (value) => {
    resume.value.education = value;
  },
});

const additionalSkills = computed({
  get: () => resume.value.additionalSkills || [],
  set: (value) => {
    resume.value.additionalSkills = value;
  },
});

const certifications = computed({
  get: () => resume.value.certifications || [],
  set: (value) => {
    resume.value.certifications = value;
  },
});

const projects = computed({
  get: () => resume.value.projects || [],
  set: (value) => {
    resume.value.projects = value;
  },
});

// Add function to ensure UUIDs exist on all items
function ensureUuids(resume: Resume) {
  if (!resume.contactInfo.uuid) {
    resume.contactInfo.uuid = generateUuid();
  }
  if (!resume.summary.uuid) {
    resume.summary.uuid = generateUuid();
  }

  resume.workExperience?.forEach((work) => {
    if (!work.uuid) work.uuid = generateUuid();
    work.achievements?.forEach((achievement) => {
      if (!achievement.uuid) achievement.uuid = generateUuid();
    });
  });

  resume.education?.forEach((edu) => {
    if (!edu.uuid) edu.uuid = generateUuid();
    edu.modules?.forEach((module) => {
      if (!module.uuid) module.uuid = generateUuid();
    });
  });

  resume.projects?.forEach((project) => {
    if (!project.uuid) project.uuid = generateUuid();
    project.achievements?.forEach((achievement) => {
      if (!achievement.uuid) achievement.uuid = generateUuid();
    });
  });

  resume.activities?.forEach((activity) => {
    if (!activity.uuid) activity.uuid = generateUuid();
    activity.achievements?.forEach((achievement) => {
      if (!achievement.uuid) achievement.uuid = generateUuid();
    });
  });

  resume.certifications?.forEach((cert) => {
    if (!cert.uuid) cert.uuid = generateUuid();
  });

  resume.additionalSkills?.forEach((skill) => {
    if (!skill.uuid) skill.uuid = generateUuid();
  });
}

// Watch for changes in the current section
watch(
  () => currentSection.value,
  (newValue) => {
    if (newValue === "optimize") {
      // No need to fetch applications as the component will handle it
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(BreadcrumbNav, {
      "back-link": "/resumes",
      "back-text": "Resumes",
      "current-page": "Resume Editor"
    }),
    _withDirectives(_createVNode(ResumeNavigation, {
      modelValue: currentSection.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentSection).value = $event)),
      resume: resume.value
    }, null, 8, ["modelValue", "resume"]), [
      [_vShow, resume.value]
    ]),
    _withDirectives(_createElementVNode("div", {
      class: _normalizeClass(["edit-content", { 'preview-mode': currentSection.value === 'preview', loading: isLoading.value }])
    }, [
      _withDirectives(_createVNode(PdfPreview, {
        loading: loading.value,
        "pdf-url": pdfUrl.value
      }, null, 8, ["loading", "pdf-url"]), [
        [_vShow, currentSection.value === 'preview']
      ]),
      _createElementVNode("div", _hoisted_2, [
        _withDirectives(_createElementVNode("div", null, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("button", {
              class: "btn btn-outline-primary flex-grow-1",
              onClick: downloadDocument
            }, [
              _hoisted_4,
              _createTextVNode(" Download Resume ")
            ]),
            (hasUnsavedChanges.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 0,
                  class: "btn btn-primary flex-grow-1",
                  onClick: saveResume
                }, [
                  _hoisted_5,
                  _createTextVNode(" Save ")
                ]))
              : _createCommentVNode("", true)
          ]),
          _createVNode(TemplateSelector, {
            modelValue: resume.value.template,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((resume.value.template) = $event))
          }, null, 8, ["modelValue"])
        ], 512), [
          [_vShow, currentSection.value === 'preview']
        ]),
        _withDirectives(_createElementVNode("div", _hoisted_6, [
          (hasUnsavedChanges.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("button", {
                  class: "btn-save-resume",
                  onClick: saveResume
                }, [
                  _hoisted_8,
                  _createTextVNode("Save Changes ")
                ])
              ]))
            : _createCommentVNode("", true),
          (currentSection.value === 'contact')
            ? (_openBlock(), _createBlock(ContactSection, {
                key: 1,
                modelValue: resume.value.contactInfo,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((resume.value.contactInfo) = $event))
              }, null, 8, ["modelValue"]))
            : (currentSection.value === 'summary')
              ? (_openBlock(), _createBlock(SummarySection, {
                  key: 2,
                  modelValue: resume.value.summary,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((resume.value.summary) = $event))
                }, null, 8, ["modelValue"]))
              : (currentSection.value === 'work')
                ? (_openBlock(), _createBlock(WorkExperienceSection, {
                    key: 3,
                    modelValue: workExperience.value,
                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((workExperience).value = $event))
                  }, null, 8, ["modelValue"]))
                : (currentSection.value === 'education')
                  ? (_openBlock(), _createBlock(EducationSection, {
                      key: 4,
                      modelValue: education.value,
                      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((education).value = $event))
                    }, null, 8, ["modelValue"]))
                  : (currentSection.value === 'additional')
                    ? (_openBlock(), _createBlock(AdditionalSection, {
                        key: 5,
                        skills: additionalSkills.value,
                        "onUpdate:skills": _cache[6] || (_cache[6] = ($event: any) => ((additionalSkills).value = $event)),
                        certifications: certifications.value,
                        "onUpdate:certifications": _cache[7] || (_cache[7] = ($event: any) => ((certifications).value = $event)),
                        projects: projects.value,
                        "onUpdate:projects": _cache[8] || (_cache[8] = ($event: any) => ((projects).value = $event))
                      }, null, 8, ["skills", "certifications", "projects"]))
                    : (currentSection.value === 'optimize')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                          _hoisted_10,
                          _hoisted_11,
                          _createElementVNode("div", _hoisted_12, [
                            _createElementVNode("div", _hoisted_13, [
                              _createVNode(ApplicationSelectorWithModal, {
                                selectorDescription: "Select an application to optimize your resume for",
                                redirectType: "resume",
                                resumeId: _unref(resumeId),
                                redirectRoute: "AIResumeEditorWithResume"
                              }, null, 8, ["resumeId"])
                            ])
                          ])
                        ]))
                      : _createCommentVNode("", true)
        ], 512), [
          [_vShow, currentSection.value !== 'preview']
        ])
      ])
    ], 2), [
      [_vShow, resume.value]
    ]),
    _createVNode(DownloadModal, {
      ref_key: "downloadModal",
      ref: downloadModal,
      resume: resume.value
    }, null, 8, ["resume"]),
    _createVNode(UpgradeResumeModal, {
      ref_key: "upgradeResumeModal",
      ref: upgradeResumeModal,
      resumeId: _unref(resumeId),
      onUpgradeComplete: handleUpgradeComplete,
      onViewResume: viewNewResume
    }, null, 8, ["resumeId"])
  ]))
}
}

})